/**
 * Мгновенная подсветка отдельных ячеек — мимо реактивности Vue.
 *
 * ЗАЧЕМ ТАК. Раньше состояние «сохраняется / сохранено / ошибка» и подсветка
 * чужих правок лежали в реактивных объектах стора, а каждая ячейка читала их
 * при вычислении своих классов. В итоге любая правка ОДНОЙ ячейки заставляла
 * Vue пересчитать классы у ВСЕХ ячеек на экране — а их 500–800. И так три раза
 * подряд: «сохраняется» → «сохранено» → «обычное». Отсюда и мигание всей
 * таблицы при правке одного поля.
 *
 * Эти подсветки живут доли секунды и не влияют ни на какую логику, поэтому
 * их место — в DOM, а не в состоянии приложения. Здесь класс ставится
 * напрямую на нужный элемент: одна ячейка вместо восьмисот.
 *
 * Ячейка может быть прокручена за пределы окна виртуализации — тогда элемента
 * нет и подсветку ставить некуда. Это нормально: подсвечивать невидимое незачем.
 */

const CELL_ATTR = 'data-cell';

/** Ключ ячейки — он же значение data-атрибута в разметке грида. */
export function cellKey(rowId: string, field: string): string {
  return `${rowId}:${field}`;
}

/**
 * Подсветка — украшение поверх готовой разметки, и её отсутствие ничего
 * не ломает. Вне браузера (тесты стора) её просто нет: иначе проверка
 * обновления строки падала бы на `document is not defined`, хотя к самому
 * обновлению это отношения не имеет.
 */
function findCell(rowId: string, field: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLElement>(
    `[${CELL_ATTR}="${CSS.escape(cellKey(rowId, field))}"]`,
  );
}

/** Таймеры снятия подсветки, чтобы повторная правка не оставляла хвостов. */
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function clearLater(key: string, el: HTMLElement, classes: string[], delay: number): void {
  const existing = timers.get(key);
  if (existing) clearTimeout(existing);
  timers.set(
    key,
    setTimeout(() => {
      el.classList.remove(...classes);
      timers.delete(key);
    }, delay),
  );
}

const SAVE_CLASSES = ['cell-saving', 'cell-saved', 'cell-error'];

export function markSaving(rowId: string, field: string): void {
  const el = findCell(rowId, field);
  if (!el) return;
  el.classList.remove(...SAVE_CLASSES);
  el.classList.add('cell-saving');
}

export function markSaved(rowId: string, field: string): void {
  const el = findCell(rowId, field);
  if (!el) return;
  el.classList.remove(...SAVE_CLASSES);
  el.classList.add('cell-saved');
  clearLater(cellKey(rowId, field), el, SAVE_CLASSES, 700);
}

export function markError(rowId: string, field: string): void {
  const el = findCell(rowId, field);
  if (!el) return;
  el.classList.remove(...SAVE_CLASSES);
  el.classList.add('cell-error');
  clearLater(cellKey(rowId, field), el, SAVE_CLASSES, 2500);
}

/** Короткая вспышка на ячейке, которую изменил другой пользователь. */
export function markRemoteChanged(rowId: string, field: string): void {
  const el = findCell(rowId, field);
  if (!el) return;
  el.classList.remove('cell-remote-changed');
  // Перезапуск CSS-анимации: без принудительного reflow повторное
  // добавление класса ничего не даст
  void el.offsetWidth;
  el.classList.add('cell-remote-changed');
  clearLater(`${cellKey(rowId, field)}:remote`, el, ['cell-remote-changed'], 1400);
}

/**
 * Ячейки, которые прямо сейчас правят другие. Обновляется дифом: снимаем
 * подсветку с тех, кто перестал редактировать, ставим новым.
 */
let currentRemoteEditing = new Map<string, string>();

export function syncRemoteEditing(next: Map<string, string>): void {
  for (const [key] of currentRemoteEditing) {
    if (next.has(key)) continue;
    const [rowId, field] = key.split(':');
    const el = rowId && field ? findCell(rowId, field) : null;
    if (el) {
      el.classList.remove('cell-remote-editing');
      el.style.removeProperty('--remote-color');
    }
  }

  for (const [key, color] of next) {
    if (currentRemoteEditing.get(key) === color) continue;
    const [rowId, field] = key.split(':');
    const el = rowId && field ? findCell(rowId, field) : null;
    if (el) {
      el.classList.add('cell-remote-editing');
      el.style.setProperty('--remote-color', color);
    }
  }

  currentRemoteEditing = new Map(next);
}

/**
 * После перерисовки окна виртуализации подсветка «кто-то правит» теряется:
 * элементы новые. Восстанавливаем её для видимых ячеек.
 */
export function reapplyRemoteEditing(): void {
  for (const [key, color] of currentRemoteEditing) {
    const [rowId, field] = key.split(':');
    const el = rowId && field ? findCell(rowId, field) : null;
    if (el) {
      el.classList.add('cell-remote-editing');
      el.style.setProperty('--remote-color', color);
    }
  }
  for (const [key, color] of currentRemoteCursors) {
    const [rowId, field] = key.split(':');
    const el = rowId && field ? findCell(rowId, field) : null;
    if (el) {
      el.classList.add('cell-remote-cursor');
      el.style.setProperty('--remote-color', color);
    }
  }
}

/**
 * Где стоят курсоры коллег.
 *
 * Отдельно от правки намеренно: правка означает «значение вот-вот изменится,
 * не лезь», курсор — просто «человек сейчас здесь». Одинаковой рамкой их
 * показывать нельзя, иначе таблица покроется одинаковыми обводками и
 * перестанет что-либо сообщать.
 *
 * Пометки ставятся мимо реактивности Vue — как и всё в этом файле: чужой
 * курсор двигается на каждое нажатие стрелки, и пересчитывать из-за этого
 * таблицу в сто тысяч строк нельзя.
 */
let currentRemoteCursors = new Map<string, string>();

export function syncRemoteCursors(next: Map<string, string>): void {
  for (const [key] of currentRemoteCursors) {
    if (next.has(key)) continue;
    const [rowId, field] = key.split(':');
    const el = rowId && field ? findCell(rowId, field) : null;
    if (el) {
      el.classList.remove('cell-remote-cursor');
      // Цвет снимаем только если ячейку не держит чужая правка
      if (!el.classList.contains('cell-remote-editing')) {
        el.style.removeProperty('--remote-color');
      }
    }
  }

  for (const [key, color] of next) {
    if (currentRemoteCursors.get(key) === color) continue;
    const [rowId, field] = key.split(':');
    const el = rowId && field ? findCell(rowId, field) : null;
    if (el) {
      el.classList.add('cell-remote-cursor');
      el.style.setProperty('--remote-color', color);
    }
  }

  currentRemoteCursors = new Map(next);
}
